import React from 'react';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const EditorialPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Button asChild variant="outline" className="mb-6">
          <a href="/">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </a>
        </Button>

        <h1 className="text-4xl font-bold text-gray-900 mb-4">Editorial Policy</h1>
        <p className="text-gray-600 mb-8">Last Updated: December 2025</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Mission</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our editorial content is created to provide informational and educational product comparison resources for consumers. We evaluate products using publicly available specifications, pricing analysis, customer feedback trends, and comparative research.
            </p>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our content may be periodically updated to maintain accuracy and relevance. Affiliate partnerships do not directly influence product rankings or recommendations.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Editorial Standards</h2>
            
            <div className="bg-blue-50 border-l-4 border-blue-600 p-6 mb-6">
              <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Commitment to Accuracy</h3>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <span>All product information is researched using manufacturer specifications and official sources</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <span>Pricing information is verified through multiple retailers when available</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <span>Content is regularly reviewed and updated to reflect current information</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                  <span>We correct errors promptly when brought to our attention</span>
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Research Methodology</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our product comparisons and recommendations are based on:
            </p>
            
            <div className="space-y-4 mb-6">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">1. Product Specifications</h3>
                <p className="text-gray-700">
                  We analyze technical specifications from official manufacturer sources including display size, processor capabilities, camera specifications, battery capacity, storage options, and connectivity features.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">2. Pricing Analysis</h3>
                <p className="text-gray-700">
                  We monitor pricing across multiple retailers to provide accurate price comparisons. Prices are subject to change and should be verified with retailers before purchase.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">3. Customer Feedback Trends</h3>
                <p className="text-gray-700">
                  We review aggregated customer feedback and ratings from various sources to identify common user experiences and product satisfaction trends.
                </p>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">4. Comparative Research</h3>
                <p className="text-gray-700">
                  We compare products across similar categories to help consumers understand relative features, benefits, and value propositions.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Editorial Independence</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              CompareBeforeYouBuy maintains editorial independence in all product comparisons and recommendations. While we participate in affiliate programs that may generate commissions, these partnerships do not:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Influence product rankings or order of presentation</li>
              <li>Determine which products are included in comparisons</li>
              <li>Affect the accuracy or objectivity of product information</li>
              <li>Alter our evaluation criteria or methodology</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Content Updates</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our content is periodically reviewed and updated to maintain accuracy and relevance. Updates may include:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>New product releases and model updates</li>
              <li>Price adjustments and availability changes</li>
              <li>Specification corrections or clarifications</li>
              <li>Additional product options or variants</li>
              <li>Updated market analysis and comparisons</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              The "Last Updated" date on each page indicates when content was most recently reviewed or modified.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Affiliate Relationships</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Some links on our website may generate affiliate commissions when users make purchases through partner retailers. These partnerships:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
              <li>Help support the maintenance and operation of our website</li>
              <li>Do not determine product rankings or recommendations</li>
              <li>Are clearly disclosed in our Privacy Policy and footer</li>
              <li>Do not result in additional costs to consumers</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              We may have affiliate relationships with multiple retailers for the same products, ensuring our recommendations remain unbiased.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Product Information Disclaimer</h2>
            <div className="bg-yellow-50 border-l-4 border-yellow-600 p-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                While we strive for accuracy, product specifications, pricing, and availability are subject to change without notice. We recommend:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>Verifying all information directly with retailers before purchase</li>
                <li>Confirming product specifications on manufacturer websites</li>
                <li>Checking current pricing and availability at point of sale</li>
                <li>Reading product reviews and detailed specifications before buying</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">User-Generated Content</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If we feature user reviews, comments, or feedback in the future, such content represents the opinions of individual users and not CompareBeforeYouBuy. We do not verify the accuracy of user-generated content but may moderate for inappropriate or misleading information.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Corrections Policy</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              If you identify inaccurate information on our website, please contact us:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Email: <a href="mailto:contact@comparebeforeyoubuy.shop" className="text-blue-600 hover:underline">contact@comparebeforeyoubuy.shop</a></li>
              <li>Phone: <a href="tel:08006521866" className="text-blue-600 hover:underline">08006521866</a></li>
            </ul>
            <p className="text-gray-700 leading-relaxed mb-4">
              We review all correction requests and update content promptly when errors are confirmed.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              Our website may contain links to third-party retailers, manufacturers, and other websites. We are not responsible for:
            </p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>The content, accuracy, or practices of linked websites</li>
              <li>Products, services, or transactions conducted on external sites</li>
              <li>Privacy policies or terms of service of third-party sites</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              For questions about our editorial policy or content:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-900 font-semibold mb-2">CompareBeforeYouBuy</p>
              <p className="text-gray-700">91 CC/A Phase 4 DHA Lahore</p>
              <p className="text-gray-700">Lahore, 54000</p>
              <p className="text-gray-700">Pakistan</p>
              <p className="text-gray-700 mt-4">
                Email: <a href="mailto:contact@comparebeforeyoubuy.shop" className="text-blue-600 hover:underline">contact@comparebeforeyoubuy.shop</a>
              </p>
              <p className="text-gray-700">
                Phone: <a href="tel:08006521866" className="text-blue-600 hover:underline">08006521866</a>
              </p>
              <p className="text-gray-700 mt-2">
                Hours: Monday-Friday 9am-6pm, Saturday 10am-4pm
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default EditorialPolicy;
